<template>
  <div class="relative h-full" :class="{ 'w-fit m-auto': isYouTubeShorts || isTikTok }" @click="onEmbedClick" :data-editable="block.editable">
    <!-- YouTube Videos -->
    <YouTubeEmbed v-if="isYouTube" :block="block" :is-thumbnail="isThumbnail" />

    <!-- YouTube Shorts -->
    <YouTubeShortsEmbed v-if="isYouTubeShorts" :block="block" :is-thumbnail="isThumbnail" />

    <!-- TikTok Videos -->
    <div v-if="isTikTok">
      <div
        :class="`pointer-events-auto max-w-[${CONSTANTS.TIKTOK.MAX_WIDTH}] h-[${CONSTANTS.TIKTOK.HEIGHT}] overflow-hidden rounded-lg w-fit m-auto ${block.embed.embeddable == 'Slide' ? 'mt-10' : ''}`"
        data-provider="tiktok"
        v-html="block.embed.html"
      />
    </div>

    <!-- Veed Videos -->
    <div v-if="isVeed" class="aspect-video overflow-hidden ml-8 relative w-full">
      <iframe :src="block.embed.embed_url" frameborder="0" webkitallowfullscreen mozallowfullscreen allowfullscreen style="position: absolute; top: 0; left: 0; width: 100%; height: 100%;" class="rounded-lg"></iframe>
    </div>

    <!-- Other Embeds -->
    <div v-if="isOtherEmbed">
      <div class="flex flex-col items-center justify-center" data-provider="other">
        <iframe
          class="w-10/12 rounded-lg"
          id="ytplayer"
          type="text/html"
          :width="CONSTANTS.OTHER_EMBED.WIDTH"
          :height="CONSTANTS.OTHER_EMBED.HEIGHT"
          :src="block.embed.original_url"
          allow="fullscreen"
          frameborder="0">
        </iframe>
      </div>
    </div>

    <!-- Placeholder Image -->
    <div v-else-if="isPlaceholder" class="relative w-full h-full object-cover rounded-lg overflow-hidden p-6" data-provider="placeholder">
      <img :src="block.embed.placeholder_image_url" class="w-full h-full object-cover rounded-lg" />
    </div>

    <!-- Video Upload Overlay -->
    <VideoUploadOverlay
      v-if="block.editable"
      :width="overlayWidth"
      :height="overlayHeight"
      :clip-path="overlayClipPath"
    />
  </div>
</template>

<script>
import { toRef, inject, watch } from 'vue'
import VideoUploadOverlay from '../../embeds/VideoUploadOverlay.vue'
import YouTubeEmbed from '../../embeds/YouTubeEmbed.vue'
import YouTubeShortsEmbed from '../../embeds/YouTubeShortsEmbed.vue'
import { useEmbedHelpers } from '../../../composables/useEmbedHelpers'
import { useSidePanelHelpers } from '../../../composables/useSidePanelHelpers'
import { t } from '@/libs/i18n'

export default {
  name: 'Embed',
  components: {
    VideoUploadOverlay,
    YouTubeEmbed,
    YouTubeShortsEmbed
  },
  props: {
    block: {
      type: Object,
      required: true
    },
    isThumbnail: {
      type: Boolean,
      default: false
    }
  },
  setup(props) {
    const embedRef = toRef(props.block, 'embed')
    const { replaceInStack } = useSidePanelHelpers()

    const onEmbedClick = () => {
      if (!props.block.editable) {
        return
      }

      replaceInStack({
        content: {
          title: t('slides_editor.components.video_form.video_settings'),
          component: 'VideoForm',
          props: { block: props.block }
        }
      })
    }

    const {
      CONSTANTS,
      isYouTube,
      isYouTubeShorts,
      isTikTok,
      isVeed,
      isOtherEmbed,
      isPlaceholder,
      overlayWidth,
      overlayHeight,
      overlayClipPath,
      youtubeWrapperClass,
      youtubeStyle,
      youtubeVideoClass
    } = useEmbedHelpers(embedRef)

    const updateProvider = (embedRef, newUrl) => {
      const providers = [
        { key: 'youtube.com/shorts', value: 'youtube_shorts' },
        { key: 'youtu.be',            value: 'youtube' },
        { key: 'youtube.com',         value: 'youtube' },
        { key: 'tiktok',              value: 'tiktok' },
      ]

      const match = providers.find(provider => newUrl?.includes(provider.key))
      embedRef.value.provider = match ? match.value : 'placeholder'
    }

    watch(
      () => [props.block.embed.original_url],
      ([newUrl], [oldUrl]) => {
        if (!props.block.embed.id) return

        updateProvider(embedRef, newUrl)
      }
    )

    return {
      CONSTANTS,
      isYouTube,
      isYouTubeShorts,
      isTikTok,
      isVeed,
      isOtherEmbed,
      isPlaceholder,
      overlayWidth,
      overlayHeight,
      overlayClipPath,
      onEmbedClick,
      youtubeStyle
    }
  }
}
</script>
